import type { NuxtError } from '#app';
import type { NloApiError } from '#imports';
import type { components } from '~/generated/portal-api';
import { assertUnreachable } from '~/utils/exhaustiveness-check';

const GENERIC_ERROR_MESSAGE = 'Er is iets mis gegaan, probeer het later opnieuw';

export function getPortalError(input: any) {
	const nloError = getNloError(input);
	// Do not check for specific ApiError instances, adjusted for unit-testing, see https://github.com/danielroe/nuxt-vitest/issues/366
	if (input && input.data && input.data._problemDetailsType === 'ErrorResultProblemDetails') {
		// Error types coming from the Portal C# api will have problem details specifically typed for LCD.
		return new PortalApiError(input);
	}
	return nloError;
}

/**
 * Throws an error if the provided error is an instance of NloNotAuthenticatedError.
 *
 * For example, directly in a watcher:
 * watch(asyncData.error, throwIfNotAuthenticated);
 *
 * @param {Error | NuxtError | null | undefined} error - The error to check for authentication.
 * @throws {NloNotAuthenticatedError} - If the error is an instance of NloNotAuthenticatedError.
 */
export function throwIfNotAuthenticated(error: Error | NuxtError | null | undefined) {
	if (!error) {
		return;
	}
	const portalError = error instanceof NloError ? error : getPortalError(error);
	if (portalError instanceof NloNotAuthenticatedError) {
		throw portalError;
	}
}

export class PortalApiError extends NloError {
	status: number;
	problemDetails?: components['schemas']['ErrorResultProblemDetails'];

	constructor(apiError: NloApiError) {
		const problemDetails = apiError.data as components['schemas']['ErrorResultProblemDetails'];
		// super(PortalApiError.getErrorMessage(problemDetails.errorCode));
		super({ message: PortalApiError.getErrorMessage(problemDetails.errorCode), isFatal: apiError.status >= 500 });
		this.problemDetails = problemDetails;
		this.status = apiError.status;
	}

	/**
	 * Returns the error message for the given error code.
	 * Please note that this message is already available through LcdApiError's message property.
	 * @param errorCode the error code from the C# API.
	 * @returns the error message (in Dutch) for the user
	 */
	static getErrorMessage(errorCode?: components['schemas']['ErrorResultProblemDetails']['errorCode']) {
		switch (errorCode) {
			case 'RateLimitError':
			case 'RecaptchaNotValidError': {
				return 'Je hebt te veel verzoeken in korte tijd verstuurd, vink ter verificatie de recaptcha aan.';
			}
			case 'LookupAddressNotFoundError': {
				return 'Deze postcode/huisnummer combinatie herkennen we niet. Neem contact op met de klantenservice';
			}
			case 'PasswordInvalidError': {
				return 'Het wachtwoord was niet correct';
			}
			case 'AccountNameAlreadyTakenError': {
				return 'Er bestaat al een account met het opgegeven e-mail adres.';
			}
			case 'IbanAlreadyInUseError': {
				return 'Er is al een account met dit IBAN (Rekeningnummer). Neem contact op met onze klantenservice.';
			}
			case 'AccountMustBeValidatedError':
			case 'AccountAlreadyActivatedError':
			case 'AccountNotFoundError':
			case 'ActionExpiredError':
			case 'ActionNotFoundError':
			case 'CannotUpdateAccountError':
			case 'DepositLimitReachedError':
			case 'InvalidPlayerStateError':
			case 'InvalidWithdrawAmountError':
			case 'MaximumNumberOfParticipationsReachedError':
			case 'NotEnoughMoneyError':
			case 'PasswordNotAllowedError':
			case 'UserAlreadyParticipatedError':
			case 'VoucherCodeAlreadyClaimedError':
			case 'VoucherCodeNotFoundError':
			case 'FormSubmitAlreadyParticipatedError':
			case 'FormSubmitValidationError':
			case 'AuthenticationError':
			case 'BankAccountAlreadyInUseError':
			case 'DuplicatePlayerFoundError':
			case 'EmailAlreadyValidatedError':
			case 'InvalidIbanError':
			case 'InvalidValidationCodeError':
			case 'PasswordNotSafeError':
			case 'PasswordTooShortError':
			case 'PoBoxAddressNotAllowedError':
			case 'UnknownEmailValidationException':
			case 'UnknownPlayerError':
			case 'InvalidAccountStatusError':
			case 'ExistingOfflineUserError':
			case undefined: {
				return GENERIC_ERROR_MESSAGE;
			}
			default:
				assertUnreachable(errorCode);
		}
	}
}

export function unwrapPortalApiError(error: Error): PortalApiError | Error {
	if (error instanceof PortalApiError) {
		return error;
	}

	if (error.cause instanceof Error) {
		return unwrapPortalApiError(error.cause);
	}

	// No PortalApiError found in the chain, create a new one
	return getPortalError(error);
}
